/**
 * [PM-SG 2026-08-12] RACE — page one. Every PM gets their own track.
 *
 * Built from how the sport actually broadcasts itself, not from how a spreadsheet looks:
 *
 *   ONE ROW IS BOTH THE TIMING TOWER AND THE TRACK. The board this replaces kept a table on
 *   the left and a track on the right, showing the same people twice and giving each of them
 *   half the room. Here the lane carries position, driver, track, figure, GAP and INTERVAL —
 *   one object, read left to right, like a broadcast timing row.
 *
 *   GAP *AND* INTERVAL. Broadcast timing always shows both because they answer different
 *   questions: GAP is your distance to the leader, INTERVAL is your distance to the person
 *   directly ahead. Showing only GAP tells nine people out of ten that they are losing;
 *   INTERVAL tells them about the fight they are actually in.
 *
 *   MINI-SECTORS AS FORM. Each lane carries a strip of one segment per day of the month, lit
 *   on days that PM gained, and coloured the way F1 colours sectors — purple for the best day
 *   anyone in the field has had, green for that PM's own best, white for an ordinary gain.
 *   It answers the question no figure on the old board could: is this person grinding steadily,
 *   or did they have one big day a fortnight ago and stop?
 *
 * MOTION. The old board suppressed motion almost entirely, which is why it reads as dead. The
 * rule here is not "less" but "honest": motion that ENCODES something fires only when that
 * thing really changed (arrival, overtake, a value moving), while motion that is merely LIFE
 * (wheels, heat, the kerb highlight, the lap) encodes nothing and is free to run continuously.
 * Nothing loops in a way that implies a number moved.
 *
 * SIZING. Everything is in viewport units with no pixel ceilings. The previous board capped
 * type in px, so on a better television it got physically SMALLER — 23px on a 65" 4K panel is
 * 8.6mm, legible to about two metres. Sized against the panel, the same row reads from six.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  SideCarSvg, SIDE_CAR_RATIO, Exhaust, prefersReducedMotion,
  LANE_SURFACE, RED, AMBER, PURPLE, GREEN, LINE, TEXT, EASE, kerb,
} from './chrome';
import { Runner, LeaderData, fmtNum } from './raceModel';
import { armAudio } from './sound';

/* ═════════════════════════════════════════════════════════ motion ═════ */

const rowIn = keyframes`from { opacity: 0; transform: translateX(-57.6px); }`;
const flashGain = keyframes`
  0%   { box-shadow: inset 0 0 0 2px ${GREEN}, inset 0 0 40px rgba(0,210,106,.28); }
  100% { box-shadow: inset 0 0 0 2px transparent, inset 0 0 40px transparent; }
`;
const flashLoss = keyframes`
  0%   { box-shadow: inset 0 0 0 2px ${RED}, inset 0 0 40px rgba(225,6,0,.24); }
  100% { box-shadow: inset 0 0 0 2px transparent, inset 0 0 40px transparent; }
`;
const battle = keyframes`0%,100% { opacity: .22; } 50% { opacity: .7; }`;
/** The centre line, travelling right to left by exactly one dash — see Surface's ::before. */
const roadRun = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(calc(-1 * var(--dash))); }
`;
const purpleFlash = keyframes`
  0%, 100% { box-shadow: inset 0 0 0 0 transparent; }
  35%      { box-shadow: inset 0 0 0 2px ${PURPLE}, inset 0 0 50px rgba(176,38,255,.3); }
`;

/* ═════════════════════════════════════════════════════════ layout ═════ */

const Wrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* One shared column rhythm for the header, the column labels and every lane, so the whole
     page lines up vertically the way a broadcast graphic does. */
  --c-pos: 65.28px;
  --c-bar: 6.14px;
  /* A FALLBACK ONLY. The real width is measured from the actual names and written back over
     this on mount and on every resize — see the probe below. A clamp cannot know that this
     month's longest name is "Shobhit Srivastava", so it either truncates somebody or leaves a
     band of dead space before the track. Measuring gives an exact hug and stays correct when
     the roster changes. */
  --c-name: 288px;
  --c-metric: 210px;
  --lane-gap: 10.56px;
  /**
   * The car's size, and therefore its travel. Position is calc(x * (100% - carw)) so a car at
   * x=1 sits flush against the flag instead of half off the end of its own track — which means
   * the width has to be a real value the maths can read, not a guess.
   *
   * ⚠ HEIGHT IS NOT A FREE DIAL. The car is 3.125:1, so every 1vh of height costs 3.125vh of
   * LENGTH, and that length comes out of the pit lane — the one stretch of track that is already
   * short. At 1920×1080 the pit lane is ~259px; at 5.4vh the car was 182px long, leaving ~77px
   * of travel to be shared by six PMs. Raising this to 7vh would leave ~22px and the pit lane
   * would stop meaning anything at all.
   *
   * 6.0vh is the ceiling that still leaves the back of the grid readable: ~65px tall, ~203px
   * long, ~57px of pit travel. The race zone is barely touched either way — it is 1030px wide,
   * so it can absorb a longer car without noticing. Going beyond 6.0vh needs GATE_X moved back
   * first, not this number raised on its own.
   */
  --carh: 64.8px;
  --carw: calc(var(--carh) * ${SIDE_CAR_RATIO});

  /**
   * ⚠ THE CAR IS SIZED BY HEIGHT BUT CONSTRAINED BY WIDTH, so a narrow screen breaks it in a way
   * a short screen does not — and it breaks silently.
   *
   * A pit-lane car is positioned as x * (1/GATE_X) * (GATE_X * 100% − carw). Once carw exceeds
   * the pit lane's own width that bracket goes NEGATIVE and every unclassified car walks off the
   * left-hand edge instead of sitting in its zone. Worked at 800px wide: the track is roughly
   * 440px after the position, name and metric columns, so the pit lane is about 88px — while a
   * 6vh car on a 768px-tall screen is around 144px long. Three times too long to fit.
   *
   * The car therefore steps DOWN with the viewport's width, not just its height. These are not
   * cosmetic breakpoints; each one keeps carw inside GATE_X of the remaining track.
   */
  /**
   * ⚠ [PM-SG FILL 2026-08-13] THE WIDTH BREAKPOINTS THAT USED TO LIVE HERE ARE GONE.
   *
   * They stepped --carh down at 1400px, 1050px and 820px. Two things made them wrong rather than
   * merely unused. A media query measures the VIEWPORT, but this board is no longer laid out
   * against the viewport — it is a canvas of its own width that gets scaled onto the screen. So on
   * a 1366-wide laptop the 1400px rule fired and shrank the car for a layout that was still a
   * full-width canvas, while on a 4K television none of them fired at all. The breakpoint was
   * answering a question nobody was asking any more.
   *
   * And they are now unreachable regardless: the canvas width is clamped to CANVAS_MIN_W (1600),
   * so it can never be 1400 or less. Deleted rather than rewritten as container queries, because a
   * container query against the Stage would be dead code that merely looked alive.
   *
   * If the canvas clamp is ever lowered below 1600, this is the reasoning to revisit.
   */
`;

/** The column labels. Small on purpose — they are read once and then never again, so they must
 *  not compete with the figures underneath them. */
const ColHead = styled.div`
  flex: 0 0 auto;
  display: grid;
  /* [2026-08-12] GAP and INT are no longer columns. They were saying, in small type at the far
     right, the same thing the label riding beside each car already says in context — and they
     were charging two columns of track width for the repetition. The track took the width back;
     EFFICIENCY is now the last column and the only figure on the right. */
  grid-template-columns:
    var(--c-pos) var(--c-bar) var(--c-name) 1fr var(--c-metric);
  gap: var(--lane-gap);
  align-items: end;
  padding: 6.48px 11.52px 4.86px;

  span {
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0.22em;
    /* [2026-08-12] Column labels read white, not dimmed grey — asked for on a wall display. */
    color: #fff;
    opacity: 1;
    white-space: nowrap;
  }
  .r { text-align: right; }
  .c { text-align: center; }
`;

/** [2026-08-12] A SECOND column heading, dropped into the field at the point the classified cars
 *  run out. Below that line the big figure stops being EFFICIENCY — the pit-lane cars are showing
 *  RETENTION, a different quantity on a different scale — and a single heading at the top of the
 *  page can only ever name one of the two. Same size and tracking as ColHead so it reads as the
 *  same kind of thing, and it only occupies the metric column. */
const SplitHead = styled.div`
  flex: 0 0 auto;
  display: grid;
  grid-template-columns:
    var(--c-pos) var(--c-bar) var(--c-name) 1fr var(--c-metric);
  gap: var(--lane-gap);
  align-items: end;
  /* [2026-08-12] Taller than a column head, on purpose. This band is not a label any more, it is
     the one instruction on the page, and at the old height the message was small enough that a
     reader had to walk up to the screen. The height comes out of the lanes (~2px each across ten
     of them), which they can afford far more easily than the message can afford being unread. */
  padding: 9.18px 0 7.56px;

  > span {
    grid-column: 5;
    text-align: right;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0.22em;
    color: #fff;
    white-space: nowrap;
  }
`;

/** The crawl: the strip slides LEFT, so the text enters at the right-hand edge and travels to the
 *  left, which is how every broadcast ticker runs. Words arrive in reading order — the first word
 *  of the sentence is the first one on screen — where a strip sliding right shows you the end of
 *  the sentence first. Half the strip's own width per cycle is what makes the loop seamless. */
const crawl = keyframes`
  from { transform: translate3d(0, 0, 0); }
  to   { transform: translate3d(-50%, 0, 0); }
`;

/**
 * THE CUT LINE.
 *
 * The band between the last classified car and the first pit-lane one was carrying one word. It
 * is also the single most important line on the board — the only thing on this page a PM can act
 * on — and it was not drawn. This states the rule ON the boundary it describes, which is where a
 * broadcast graphic puts it: F1's own qualifying cut is a marked line across the timing tower,
 * not a note somewhere else on the screen.
 *
 * The MOTION is kept deliberately quiet. The plume and the cars are the only things on this page
 * whose movement means a number, and a bright fast ticker under them would be the light sweep all
 * over again — the brightest, fastest thing on screen saying nothing. So: one message, dim, and a
 * crawl slow enough (~80px/s at 1080p) to read at a glance rather than demand to be watched.
 */
const CutLine = styled.div`
  grid-column: 1 / 5;
  align-self: stretch;
  position: relative;
  display: flex;
  align-items: center;
  gap: 13.44px;

  /* [2026-08-12] The red-and-white kerb stripe that used to run along the top edge of this band
     is REMOVED. The page already carries kerbing top and bottom of the field (the kerb mixin in
     chrome.tsx); a third one cutting across the middle read as a hard rule slicing the board in
     two rather than as track furniture, and the boundary is already stated by the CUT LINE tag,
     the crawl and the wash below. Do not reinstate it without re-checking that. */
  /* A shallow red wash that fades out downwards — the same trick a broadcast graphic uses to
     seat a strip on the picture instead of laying it on top. */
  background: linear-gradient(180deg, rgba(225, 6, 0, 0.10), rgba(225, 6, 0, 0) 78%);

  /**
   * THE TAG. Fixed, never moving, pinned on the line it names — the FIA/broadcast convention for
   * a marked boundary (the qualifying cut is labelled at the line, not in a caption elsewhere).
   * It also guarantees the band is legible at a glance even when the crawl is mid-sentence.
   * Chamfered, not rounded: the board's geometry is cut corners throughout.
   */
  .tag {
    flex: 0 0 auto;
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.55em;
    height: 100%;
    padding: 0 16.32px 0 11.52px;
    background: linear-gradient(180deg, rgba(20, 20, 28, 0.96), rgba(12, 12, 18, 0.96));
    border-left: 4.22px solid ${RED};
    clip-path: polygon(0 0, 100% 0, calc(100% - 0.7em) 100%, 0 100%);
    font-size: 22px;
    font-weight: 900;
    letter-spacing: 0.2em;
    color: #fff;
    white-space: nowrap;
    /* The chequer block: the same repeating-conic pattern as the flag on the track, so the two
       read as the same object at two sizes. */
    &::before {
      content: '';
      width: 1.1em; height: 1.1em;
      background: repeating-conic-gradient(#E9E9F0 0 25%, #14141C 0 50%) 0 0 / 0.55em 0.55em;
      opacity: 0.9;
    }
  }

  .strip {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    /* The fade at both ends is what makes it read as a line the message travels ALONG rather than
       a box the text is inside — no visible start, no visible stop. Prefixed as well as plain:
       unprefixed mask-image is recent in Chromium, and the machine that matters here is whatever
       browser is baked into a television. */
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 4%, #000 94%, transparent);
    mask-image: linear-gradient(90deg, transparent, #000 4%, #000 94%, transparent);
  }

  /**
   * SPEED IS SET IN PIXELS PER SECOND, not in "how long a loop takes".
   *
   * One cycle carries the strip half its own width. At 1080p the message is ~1,190px and the gap
   * after it ~320px, so half of four copies is ~3,020px — 55s puts that at roughly 55px/s, well
   * under half the pace of a news crawl (120–150px/s). That is the point: a reader glancing up
   * mid-sentence has time to finish it, and nothing on the page is being out-run by its own
   * furniture.
   *
   * ⚠ THIS NUMBER IS NOT INDEPENDENT. It was 90s when the message carried a third clause; cutting
   * that clause shortened the cycle, and leaving 90s in place would have slowed the crawl to a
   * drift. Change the copy, the font size or the gap, and re-derive it: seconds = cycle px ÷ 55.
   */
  .crawl {
    display: flex;
    width: max-content;
    animation: ${crawl} 55s linear infinite;
    will-change: transform;
  }

  /* ⚠ inline-BLOCK, never inline-flex. A flex container turns each text run into an anonymous
     item and DROPS the whitespace between them, so "CLEAR <b>85% RETENTION</b> TO BE" rendered as
     "CLEAR85% RETENTIONTO BE". Normal inline flow inside the message is what keeps its spaces. */
  em {
    font-style: normal;
    display: inline-block;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: 0.2em;
    white-space: nowrap;
    color: rgba(255, 255, 255, 0.74);
    /* THE BREATHING SPACE between one message and the next repeat of it, and it is doing real
       work: without a gap this long the strip reads as one endless run-on sentence with no
       beginning, which is what made the first version unreadable rather than merely long. A
       clear pause is what tells the eye "this is where it starts again". ~320px at 1080p, about
       a quarter of the message's own width. */
    padding-right: 230.4px;
  }
  /* Figures in amber, the same amber the board already uses for "not through yet" — the pit-lane
     position marker and the battle label. A number in a sentence should carry the page's meaning
     for that colour, not a decoration of its own. */
  b { color: ${AMBER}; font-weight: 900; }
  /* Clause separator: a chequered block rather than a bullet. */
  i {
    display: inline-block;
    vertical-align: middle;
    width: 0.85em; height: 0.85em;
    margin: 0 19.2px;
    background: repeating-conic-gradient(#E9E9F0 0 25%, #14141C 0 50%) 0 0 / 0.42em 0.42em;
    opacity: 0.75;
  }

  @media (prefers-reduced-motion: reduce) {
    .crawl { animation: none; }
  }
`;

const Lanes = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0 11.52px 0;
`;

/* ───────────────────────────────────────────────────────── the lane ─── */

const Lane = styled.div<{ $lead: boolean; $flash: 'gain' | 'loss' | null }>`
  flex: 1;
  min-height: 0;
  display: grid;
  /* [2026-08-12] GAP and INT are no longer columns. They were saying, in small type at the far
     right, the same thing the label riding beside each car already says in context — and they
     were charging two columns of track width for the repetition. The track took the width back;
     EFFICIENCY is now the last column and the only figure on the right. */
  grid-template-columns:
    var(--c-pos) var(--c-bar) var(--c-name) 1fr var(--c-metric);
  gap: var(--lane-gap);
  align-items: center;
  position: relative;
  animation: ${rowIn} 0.5s ${EASE} backwards;
  animation-delay: calc(var(--i) * 55ms);

  /**
   * THE WHOLE LANE IS ONE SURFACE.
   *
   * The asphalt used to belong to the track cell alone, so there was a real boundary where it
   * began — and no amount of fading hides one, because the surface also carried an inset shadow
   * that darkened its own left edge exactly at the join. A gradient laid over a dark band still
   * reads as a band.
   *
   * So there is no join any more. Position, name and track sit on the same continuous asphalt,
   * and the track region is identified only by what is ON it: the pit wash, the gate, the flag.
   * That is also how the sport does it — timing graphics are laid OVER the track, not beside it.
   *
   * Depth comes from a purely VERTICAL inset shadow. The original was a 50px shadow on all four
   * sides, and its left and right lobes were the vertical seams themselves.
   */
  background:
    ${(p) => (p.$lead ? 'linear-gradient(90deg, rgba(176,38,255,.15), transparent 38%),' : '')}
    linear-gradient(180deg, rgba(255, 255, 255, 0.022), transparent 40%, rgba(0, 0, 0, 0.3)),
    ${LANE_SURFACE};
  box-shadow:
    inset 0 14px 26px -14px rgba(0, 0, 0, 0.55),
    inset 0 -14px 26px -14px rgba(0, 0, 0, 0.55);

  /* Grain across the FULL width, so the texture is continuous too — a grain that stopped at the
     track's edge would re-draw the very line this removes. */
  &::after {
    content: '';
    position: absolute; inset: 0; opacity: 0.16; mix-blend-mode: overlay; pointer-events: none;
    z-index: 0;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E");
  }

  /* The separator is a soft gradient that fades at both ends, not a hard dashed rule. The dashes
     were the single most dated element on the old board. */
  & + &::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.14) 18%, rgba(255,255,255,.14) 82%, transparent);
  }

  /* The leader's purple wash is layered INTO the background stack above, not declared here —
     a second background rule would replace the asphalt outright and punch a hole in the
     continuous surface for exactly one lane. */
  ${(p) => p.$flash === 'gain' && `animation: ${rowIn.getName()} .5s ${EASE} backwards, ${flashGain.getName()} 2.2s ${EASE};`}
  ${(p) => p.$flash === 'loss' && `animation: ${rowIn.getName()} .5s ${EASE} backwards, ${flashLoss.getName()} 2.2s ${EASE};`}
`;

/* Everything in the row sits ABOVE the lane's grain overlay. An absolutely-positioned pseudo
   paints over non-positioned siblings, so without this the figures would be very slightly
   washed by the texture that is meant to be under them. */
const Pos = styled.div<{ $lead: boolean }>`
  position: relative;
  z-index: 1;
  font-size: 48px;
  font-weight: 900;
  line-height: 0.9;
  letter-spacing: -0.02em;
  text-align: center;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$lead ? PURPLE : TEXT)};
`;

const Bar = styled.div`
  position: relative;
  z-index: 1;
  justify-self: stretch;
  align-self: stretch;
  margin: 14% 0;
  background: var(--c);
  box-shadow: 0 0 18px var(--g);
`;

const Who = styled.div`
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 10.56px;
  /* Above the track's fade-in, so a long name is never washed over by the seam gradient that
     now reaches back into this column. */
  position: relative;
  z-index: 2;

  /* No initials here. They were a second label for the same person sitting inches from the
     first, and on a row this size a name never needs abbreviating — the code only ever earned
     its place on the old board because the track had a narrow chip column with nothing else to
     put in it. Reclaimed for the name, which can now run longer before it truncates. */
  .nm {
    /**
     * [PM-SG 10FT 2026-08-13] 44px, up from 30.
     *
     * The room reads this board from six to ten feet, and on the 50-inch panel the legibility
     * floor for ten feet is 37px (a figure that cross-checks against the broadcast rule of
     * smallest-text ≈ 1/30 of screen height: 1080/30 = 36). At 30px the names read to about eight
     * feet — fine for whoever is standing at the screen, not for the room it is on the wall for.
     *
     * ⚠ THE PROBE MUST MATCH THIS EXACTLY. fitNames() measures a hidden span and writes the result
     * out as --c-name, the driver column's width. If the probe and this rule ever disagree, the
     * column is sized for a font nobody is looking at — too narrow and every name truncates, too
     * wide and the track loses the room for no reason.
     *
     * ⚠ AND THIS IS WHY IT FITS AT ALL: each lane is ~93px tall against a 65px car, so the height
     * was never the constraint — the type was simply small relative to the row it sat in. Measured
     * before touching it, because the assumption had been that the field would have to be halved.
     */
    font-size: 44px;
    font-weight: 700;
    letter-spacing: 0.005em;
    white-space: nowrap;
    /* No ellipsis. The column above is sized to hold the longest name outright, so anything that
       still overflowed would be a sizing bug worth SEEING rather than one quietly hidden. */
  }
`;

/* ─────────────────────────────────────────────────── the track cell ─── */

const Track = styled.div`
  position: relative;
  z-index: 1;
  align-self: stretch;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

/** Asphalt. Grain and a light falloff so a large dark area does not band on a big panel — flat
 *  fills are most of what reads as cheap on a television. */
const Surface = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  margin: 5.4px 0;
  /**
   * NOT overflow:hidden.
   *
   * The exhaust plume is born at the car's diffuser and drifts a car-length BACKWARDS, so for
   * any car sitting near the start of its track the smoke was being sliced off dead straight at
   * the surface's left edge — a hard vertical cut, in the one place the eye is already drawn.
   *
   * clip-path with a negative left inset keeps the top, right and bottom clipped exactly as
   * before (the lap sends cars far off both ends and they must not sail across the metric
   * column) while letting the plume drift out over the name area and dissolve on its own, which
   * is what it was always supposed to do.
   */
  clip-path: inset(0 0 0 -180px);
  /* No background and no shadow of its own — the lane already IS the asphalt. This element is
     now purely the coordinate space the track furniture and the car are positioned in. */

  /**
   * [2026-08-12] THE ROAD MOVES, so the cars read as driving instead of parked.
   *
   * A dashed centre line travelling right to left is the oldest trick there is for selling
   * motion — every side-scrolling frame of film does it — and it is the honest kind of motion for
   * this board: it encodes nothing. It says "these cars are running", which they are, and it
   * cannot be misread as a figure changing, because it never stops, never varies, and is
   * identical in every lane.
   *
   * ⚠ IT MOVES BY TRANSFORM, NOT background-position. The dashes repeat every --dash, and the
   * element is one whole tile wider than its lane, so translating it exactly one tile left leaves
   * the pattern in the same place it started — seamless at any width, and composited on the GPU
   * rather than repainting ten strips every frame.
   *
   * Layered at z-index 0: above the pit wash, under the gate (3), the flag (2) and the car (4).
   * Kept faint on purpose — this is the road, not a feature of it.
   */
  --dash: 42.24px;
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: calc(100% + var(--dash));
    height: 2.16px;
    margin-top: calc(2.16px / -2);
    z-index: 0;
    pointer-events: none;
    background: repeating-linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.16) 0 calc(var(--dash) * 0.55),
      transparent calc(var(--dash) * 0.55) var(--dash));
    animation: ${roadRun} 1s linear infinite;
    will-change: transform;
  }
  @media (prefers-reduced-motion: reduce) {
    &::before { animation: none; }
  }

  /* The pit lane: a soft desaturated wash, not the 45-degree hatching the old board used —
     diagonal hatching is the most dated pattern on a screen. Faded out on its left so it does
     not re-introduce an edge where the name column ends. */
  .pit {
    position: absolute; top: 0; bottom: 0; left: 0;
    background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.34) 34%, rgba(0, 0, 0, 0.16));
  }
  /* The gate: a soft band with a hard core, rather than a 2px slab that out-shouted the cars. */
  .gate {
    position: absolute; top: 0; bottom: 0; width: 17.28px; margin-left: -8.64px; z-index: 3;
    background: linear-gradient(90deg, transparent, rgba(225,6,0,.30), rgba(225,6,0,.85), rgba(225,6,0,.30), transparent);
  }
  .flag {
    position: absolute; top: 0; bottom: 0; right: 0; width: 18.24px; z-index: 2; opacity: 0.7;
    background: repeating-conic-gradient(#E9E9F0 0 25%, #14141C 0 50%) 0 0 / 9.6px 9.6px;
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 45%);
    mask-image: linear-gradient(90deg, transparent, #000 45%);
  }
  /**
   * THE LIGHT SWEEP IS GONE. [2026-08-12]
   *
   * It was a 22%-wide white panel crossing every lane on a 9s loop, staggered per row — and it
   * was the brightest moving thing on the board. Two problems, and the second is the real one:
   *
   * 1. It escaped the lane. The negative left inset on the clip-path above exists so the exhaust
   *    can drift out over the name column; the sweep used that same opening and slid a pale wash
   *    underneath the drivers' names, which is where it was first noticed.
   * 2. It competed with the smoke. The plume is the one piece of motion here that MEANS
   *    something — it marks where a car is and which way it is facing. A second, larger, brighter
   *    animation running over the same asphalt on a different clock reads as glare, and the eye
   *    goes to the glare.
   *
   * Nothing is lost by removing it. It was only ever there to prove the screen was live, and the
   * wheels, the heat haze and the plume all do that while also being about the race.
   */
`;

/**
 * ONE ZONE. [PM-SG-LB 2026-09-18]
 *
 * The two-zone mapping existed because an unclassified car must never be drawn level with a
 * classified one — that would be the board stating something untrue. The cumulative table has no
 * classification, so there is no untruth left to guard against: --x is a plain fraction of the
 * track, driven by efficiency against the leader's, and a car's distance along the lane is simply
 * how much of the leader's efficiency it holds.
 *
 * ⚠ THE LAP MAPPING AND THE RESTING MAPPING ARE NOW IDENTICAL, which is why `data-lap` is gone.
 * Everything the lap needed its escape hatch for — every car covering the same pixels at the same
 * speed, and every car leaving the screen — is true of the resting rule as well now.
 */
const Car = styled.div<{ $lead: boolean }>`
  position: absolute;
  top: 50%;
  height: var(--carh);
  width: var(--carw);
  left: calc(var(--x, 0) * (100% - var(--carw)));

  transform: translateY(-52%);
  color: var(--c);
  z-index: 4;
  transition: left 950ms ${EASE};
  filter: ${(p) => (p.$lead
    ? 'drop-shadow(0 0 18px rgba(176,38,255,.8)) drop-shadow(0 6px 12px rgba(0,0,0,.7))'
    : 'drop-shadow(0 0 16px var(--g)) drop-shadow(0 6px 12px rgba(0,0,0,.7))')};

  svg { width: 100%; height: 100%; display: block; position: relative; z-index: 2; }

  /* Every car is racing now, so every plume runs at the racing rate. */
  --puff-dur: 2.05s;
  --puff-max: 0.82;
  --puff-run: calc(var(--carw) * 0.62);

  /* The label rides WITH the car, so it always sits just off the nose and the stretch of empty
     asphalt ahead is labelled with what is actually in front of them. */
  .ahead {
    position: absolute;
    left: calc(100% + 13.44px);
    top: 50%;
    transform: translateY(-50%);
    white-space: nowrap;
    /**
     * [PM-SG 2026-08-13] 26px, up from 17.66px.
     *
     * At 17.66 canvas px this label stood 7.3mm tall on the 50-inch board, which the legibility
     * maths puts at readable to about 4.8 feet — and the room reads this board from six to ten.
     * It was carrying the one figure that says whether somebody is catching up or falling behind,
     * in the smallest type on the page.
     *
     * ⚠ 26px is a CEILING, not a preference. The driver name is 29.76px, and the moment this label
     * passes that it starts out-shouting the person it belongs to — a gap is context, a name is the
     * subject. Anything larger needs the name to grow first.
     *
     * Legibility is now about 7.1 feet. Reaching the full ten needs the type pass on the whole
     * page, not this one line: the floor for ten feet is 37px, which is above the name.
     */
    /**
     * [PM-SG 2026-08-13] 30px. It went 26 -> 37 in the ten-foot pass and came back down: on the
     * actual television 37 read as shouting, because this label sits inches from a 44px name and
     * a gap is context where the name is the subject. 30px still reads to about eight feet and is
     * clearly subordinate, which is the right trade for a figure nobody navigates by.
     */
    font-size: 30px;
    font-weight: 700;
    letter-spacing: 0.16em;
    color: rgba(255, 255, 255, 0.75);
    pointer-events: none;
    transition: opacity 220ms ease;
  }

  /**
   * ⚠ [PM-SG 2026-08-13] THE LABEL IS HIDDEN WHILE THE FIELD IS LAPPING, and this fixes a real bug
   * that only ever appeared on the television.
   *
   * The label rides with its car. fitLabels() puts it BEHIND the car (.trail) when there is no
   * room ahead — and it decides that from surface.clientWidth, which is narrower on a 16:9 TV
   * (~1157px of track) than on a laptop's wider canvas (~1541px). So on the TV the leaders'
   * labels trail; on the laptop they do not.
   *
   * Then the lap sends every car to LAP_X_OUT = 1.55, far past the flag. A TRAILING label follows
   * behind its car, and at that distance its right end has left the screen while its left end has
   * not: on the TV roughly twenty pixels of it stayed parked against the finish line for the whole
   * outbound leg. On the laptop the same label cleared the edge completely, which is exactly why
   * this was reported on one screen and not the other. The 26 -> 37px raise widened the label by
   * about 40% and pushed it into view.
   *
   * Shrinking the type alone would hide it again by luck, and would break again the first time a
   * longer figure came along. The honest fix is that this label is a RESTING-STATE annotation —
   * while the cars are lapping they are nowhere near their real positions, so a gap-to-the-leader
   * reading is meaningless anyway. It fades out for the lap and fades back when the field settles.
   */
  .lapping & .ahead { opacity: 0; }
  /* ⚠ And the battling label needs its ANIMATION killed, not just its opacity set: a running
     animation wins over a normal declaration in the cascade, so .battle would have carried on
     pulsing into view through the whole lap while every other label obeyed the rule above. */
  .lapping & .ahead.battle { animation: none; }
  /* Below this there is no empty asphalt left for it to name: the track is a few car-lengths
     wide and the label would sit across the car it belongs to. The figure it echoes is already
     in the metric column, so nothing is lost by dropping it. */
  /* [PM-SG FILL 2026-08-13] The 820px rule that hid .ahead here is gone — unreachable now that the
     canvas is clamped to 1600 wide, and it measured the viewport rather than the canvas anyway.
     A wider screen now gives the track MORE room, so this label has more space, never less. */
  /**
   * NO ROOM AHEAD? THE LABEL GOES BEHIND. [2026-08-12]
   *
   * The leaders are the ones this bites: a car parked just short of the flag has almost no track
   * left in front of it, so a label riding off its nose ran straight into the surface's right
   * edge and was cut mid-word — "+7.02" clipped to a stub. The clip is not optional, either; it
   * is what stops a car sailing across the EFFICIENCY column during a lap.
   *
   * So the label moves to the tail instead of being shortened or shrunk. The text is identical
   * and complete; only the side changes, and it changes for exactly the cars that need it. The
   * side is chosen from the car's RESTING position, computed in JS from the same two-zone
   * mapping the CSS uses — measuring the live element would flip labels every time the field
   * took a lap and went off-screen.
   */
  .ahead.trail {
    left: auto;
    right: calc(100% + 13.44px);
  }
  .ahead b { color: var(--c); font-weight: 700; }

  /* A BATTLE. When the person ahead is within touching distance, the label stops reporting the
     distance to the leader — which for most of the field is only ever bad news — and switches
     to the fight actually happening. This is the one place INTERVAL now lives, and it earns far
     more attention here, beside the car, than it did as a column of small type on the far right. */
  .ahead.battle {
    color: ${AMBER};
    animation: ${battle} 2s ease-in-out infinite;
    will-change: opacity;
  }
  .ahead.battle b { color: ${AMBER}; }
`;

/* [2026-08-12] The mini-sector strip (a box per day of the month, coloured purple/green/white)
   is GONE from the lanes. It was a second thing to read in a lane that already carries a car, a
   gap and a figure, and on a wall display nobody was reading it. The per-day `form` data still
   comes down from the API — the PIT WALL page is where a month-shape belongs, not the track. */

const Metric = styled.div`
  position: relative;
  z-index: 1;
  text-align: right;
  font-size: 48px;
  font-weight: 900;
  line-height: 0.95;
  letter-spacing: -0.015em;
  font-variant-numeric: tabular-nums;
  color: var(--c);
  /* [2026-09-18, user] NO GLOW ON TEXT. A shadow behind type thickens every stroke and fills
   its counters, which is the opposite of legible at ten feet. Glow stays on the CARS and
   the lamps - objects, where it reads as light - and comes off anything anybody has to read. */
  text-shadow: none;
`;

/** An off-screen twin of the name cell, used to measure. It MUST carry exactly the same font
 *  rules as `.nm` — a probe that is even a weight out reports a width the real text will not
 *  honour, and the column ends up either clipping or padded. */
const Probe = styled.span`
  position: absolute;
  top: -9999px;
  left: -9999px;
  visibility: hidden;
  white-space: nowrap;
  pointer-events: none;
  font-size: 44px;
  font-weight: 700;
  letter-spacing: 0.005em;
`;

const KerbRow = styled.div`
  flex: 0 0 5.94px;
  margin: 0 11.52px;
  ${kerb}
  &:last-child::after { animation-delay: -4.5s; }
`;

/* ═════════════════════════════════════════════════════════ the view ═════ */

const LAP_MS = 30000;
const LAP_OUT = 1900;
const LAP_IN = 2100;
const LAP_STAG = 70;
/* Lap-mode --x (a plain fraction of the track, see Car's [data-lap] rule). 1.0 already puts the
   car's tail exactly on the right-hand edge and 0 puts its nose on the left one, so anything past
   that is clearance: at 1920 the track is ~1200px and the car ~200px, which makes these worth
   about 550px of margin at each end. They hold until a car is a third of the track long. */
const LAP_X_OUT = 1.55;
const LAP_X_BACK = -0.55;
/** Two PMs inside this many points of each other are in a fight worth marking. */
const BATTLE_THRESHOLD = 3;

/**
 * A car's RESTING left offset, in pixels — the same mapping the Car's `left` rule does, expressed
 * in JS so the label can be placed before anything has moved.
 *
 * It is recomputed rather than read off the element on purpose. A car is off-screen for four of
 * every thirty seconds while the field laps, and a label whose side was decided from a live
 * measurement would flip to the tail on the way out and back again on the way in.
 */
function restingLeft(x: number, width: number, carw: number): number {
  return x * (width - carw);
}

/* seasonClock (today / lap / deadline, on the IST calendar) lived here to drive the strip's
   playhead. Its last reader went with the strip, so it went too — PIT WALL computes its own. */

const RaceView: React.FC<{ data: LeaderData; entryToken: number }> = ({ data, entryToken }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);
  const carRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const targetX = useRef<Map<string, number>>(new Map());
  const prevRank = useRef<Map<string, number>>(new Map());
  const lapping = useRef(false);
  const reduced = useRef(prefersReducedMotion());
  const [flash, setFlash] = useState<Map<string, 'gain' | 'loss'>>(new Map());

  const drivers = data.runners;
  const driversRef = useRef(drivers);
  driversRef.current = drivers;

  /* Positions are written imperatively and nowhere else, so a poll re-rendering the list can
     never fight the lap for ownership of --x. A car whose number did not move is not touched. */
  /* Size the DRIVER column to the widest name actually in the field, and nothing more. Re-run on
     every resize because the font is in vw — a column measured at one width is wrong at another. */
  const fitNames = useCallback(() => {
    const wrap = wrapRef.current, probe = probeRef.current;
    if (!wrap || !probe) return;
    let max = 0;
    for (const d of driversRef.current) {
      probe.textContent = d.name;
      /* ⚠ [PM-SG CANVAS 2026-08-13] offsetWidth, NOT getBoundingClientRect().width. The board is a
         1920x1080 canvas that gets scaled to the screen, and the two report different things
         under that scale: offsetWidth is CANVAS px, getBoundingClientRect is SCREEN px. The value
         measured here is written straight back out as `--c-name`, a canvas-px column width — so
         the rect version would have multiplied the driver column by the scale factor, making it
         twice too wide on a 4K panel. The probe is absolutely positioned, so it has a layout box
         and offsetWidth is exact; the integer rounding it costs is already absorbed by the +2. */
      max = Math.max(max, probe.offsetWidth);
    }
    if (max > 0) wrap.style.setProperty('--c-name', `${Math.ceil(max) + 2}px`);
  }, []);

  /* Put each distance label on the side that actually has room for it — off the nose by default,
     behind the tail when the nose is too close to the end of the track for the full text to fit.
     The gap is MEASURED off the rendered element rather than restated here, so the clamp in the
     CSS stays the only place that number is written down. */
  const fitLabels = useCallback(() => {
    for (const d of driversRef.current) {
      const car = carRefs.current.get(d.name);
      const label = car?.querySelector<HTMLElement>('.ahead');
      const surface = car?.parentElement;
      if (!car || !label || !surface) continue;

      label.classList.remove('trail');
      const width = surface.clientWidth;
      const carw = car.offsetWidth;
      const gap = label.offsetLeft - carw;
      const nose = restingLeft(d.x, width, carw) + carw;
      if (nose + gap + label.offsetWidth > width) label.classList.add('trail');
    }
  }, []);

  /* fitLabels runs AFTER fitNames, never before: the driver column's width is what is left over
     for the track, so measuring the track first would measure the wrong track. */
  useLayoutEffect(() => { fitNames(); fitLabels(); }, [fitNames, fitLabels, drivers]);

  useEffect(() => {
    const ro = new ResizeObserver(() => { fitNames(); fitLabels(); });
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [fitNames, fitLabels]);

  const setX = (car: HTMLDivElement, x: number) => {
    car.dataset.x = String(x);
    car.style.setProperty('--x', String(x));
  };

  /* [PM-SG-LB 2026-09-18] lapX() is gone. It converted a two-zone resting pixel into a
     one-zone lap fraction so the change of mapping was never drawn. With one zone, --x already
     IS that fraction and the conversion is the identity. */

  /** Change --x WITHOUT drawing it: kill the transition, write, reflow, restore. */
  const jumpTo = (car: HTMLDivElement, x: number) => {
    car.style.transition = 'none';
    car.style.transitionDelay = '';
    setX(car, x);
  };

  useLayoutEffect(() => {
    drivers.forEach((d) => {
      targetX.current.set(d.name, d.x);
      const car = carRefs.current.get(d.name);
      if (!car || lapping.current) return;
      if (car.dataset.x === String(d.x)) return;
      setX(car, d.x);
    });
  }, [drivers]);

  /* Overtakes. A position change flashes the lane once — green up, red down — and then wipes
     away, which is exactly what the broadcast package does rather than leaving a highlight on. */
  useEffect(() => {
    const next = new Map<string, 'gain' | 'loss'>();
    drivers.forEach((d, i) => {
      const was = prevRank.current.get(d.name);
      if (was !== undefined && was !== i) next.set(d.name, i < was ? 'gain' : 'loss');
      prevRank.current.set(d.name, i);
    });
    if (!next.size) return;
    setFlash(next);
    const t = window.setTimeout(() => setFlash(new Map()), 2400);
    return () => window.clearTimeout(t);
  }, [drivers]);

  /* The lap. Out through the flag, round, and back in from the left to exactly the positions
     each car's own number gives it — read from targetX, not from where it departed, so a poll
     landing mid-lap simply settles the field on the newer figures. The wrap happens with the
     transition off, so the jump back is never drawn. */
  const runLap = useCallback(() => {
    if (lapping.current || document.hidden || reduced.current) return;
    const field = driversRef.current;
    const cars = field.map((d) => carRefs.current.get(d.name)).filter(Boolean) as HTMLDivElement[];
    if (cars.length !== field.length || !cars.length) return;

    /* Enter the lap standing still, on the position each car already holds. Under the old
       two-zone mapping this needed a conversion; with one zone --x IS the track fraction. */
    const start = cars.map((car, i) => targetX.current.get(field[i].name) ?? field[i].x);

    lapping.current = true;
    wrapRef.current?.classList.add('lapping');
    cars.forEach((car, i) => jumpTo(car, start[i]));
    void cars[0].offsetWidth;

    cars.forEach((car, i) => {
      car.style.transition = '';
      car.style.transitionDuration = `${LAP_OUT}ms`;
      car.style.transitionDelay = `${i * LAP_STAG}ms`;
      setX(car, LAP_X_OUT);
    });

    window.setTimeout(() => {
      cars.forEach((car) => jumpTo(car, LAP_X_BACK));
      void cars[0].offsetWidth;
      /* Where each car is landing is read fresh, not remembered: a poll during the lap can have
         moved a PM, and the car should settle on the newer figure. */
      const back = field.map((d) => driversRef.current.find((n) => n.name === d.name) || d);
      const home = back.map((d) => targetX.current.get(d.name) ?? d.x);
      cars.forEach((car, i) => {
        car.style.transition = '';
        car.style.transitionDuration = `${LAP_IN}ms`;
        car.style.transitionDelay = `${i * LAP_STAG}ms`;
        setX(car, home[i]);
      });
      window.setTimeout(() => {
        cars.forEach((car, i) => { jumpTo(car, home[i]); car.style.transitionDuration = ''; });
        void cars[0].offsetWidth;
        cars.forEach((car) => { car.style.transition = ''; });
        lapping.current = false;
        wrapRef.current?.classList.remove('lapping');
      }, LAP_IN + (cars.length - 1) * LAP_STAG + 150);
    }, LAP_OUT + (cars.length - 1) * LAP_STAG + 120);
  }, []);

  useEffect(() => {
    /* Empty deps deliberately: keyed on `drivers` this interval was torn down by every poll, and
       poll and lap share a thirty-second beat, so it reset just before it was ever due to fire. */
    const t = window.setInterval(runLap, LAP_MS);
    return () => window.clearInterval(t);
  }, [runLap]);

  /* Arm the gesture unlock here as well as on the announcement page: RACE is the page on screen
     most of the time, so it is the most likely place for anybody's one tap to land. */
  useEffect(() => armAudio(), []);

  /* Arrival: the field drives in from the left, staggered, every time the page is opened. */
  useEffect(() => {
    if (reduced.current) return;
    const field = driversRef.current;
    const cars = field.map((d) => carRefs.current.get(d.name)).filter(Boolean) as HTMLDivElement[];
    if (cars.length !== field.length || !cars.length) return;
    lapping.current = true;
    wrapRef.current?.classList.add('lapping');
    /* The arrival starts off-screen left, so the whole field drives in. */
    cars.forEach((car) => jumpTo(car, LAP_X_BACK));
    void cars[0].offsetWidth;
    const t = window.setTimeout(() => {
      const home = field.map((d) => targetX.current.get(d.name) ?? d.x);
      cars.forEach((car, i) => {
        car.style.transition = '';
        car.style.transitionDuration = '1500ms';
        car.style.transitionDelay = `${i * 80}ms`;
        setX(car, home[i]);
      });
      window.setTimeout(() => {
        cars.forEach((car, i) => { jumpTo(car, home[i]); car.style.transitionDuration = ''; });
        void cars[0].offsetWidth;
        cars.forEach((car) => { car.style.transition = ''; });
        lapping.current = false;
        wrapRef.current?.classList.remove('lapping');
      }, 1500 + cars.length * 80 + 200);
    }, 220);
    return () => window.clearTimeout(t);
  }, [entryToken]);

  return (
    <Wrap ref={wrapRef}>
      <Probe ref={probeRef} aria-hidden="true" />
      {/* [2026-08-12] The title bar is gone — no wordmark, no round, no lap counter. On a wall
          display the page is identified by what is ON it, and that strip was charging the track
          roughly seventy pixels of height to repeat things the room already knows. The whole
          screen is now the race.
          ROUND / LAP / FASTEST LAP have no home on this page any more; PIT WALL is where
          they belong. */}
      <ColHead>
        <span className="c">POS</span>
        <span />
        {/* PMs, not DRIVER: the metaphor is the board's, but the column is a list of the actual
            people reading it, and they call themselves PMs. */}
        <span>PMs</span>
        {/* [PM-SG-LB 2026-09-18] The track is one zone now, so the head names what the whole
            lane means rather than the two halves it used to be split into. */}
        <span className="c">
          THE FIELD · BENCHMARK ${fmtNum(data.effectiveInventory, 0)} · CUMULATIVE SINCE AUGUST
        </span>
        <span className="r">EFFICIENCY</span>
      </ColHead>

      <KerbRow />

      <Lanes>
        {drivers.map((d: Runner, i: number) => {
          const intervalNum = Number(String(d.interval).replace(/[^0-9.-]/g, ''));
          /* Every interval is now to a PERSON — there is no gate for the first car of a zone
             to be measured against, so the class test that used to guard this is gone. */
          const ahead = i > 0 ? drivers[i - 1] : null;
          const aheadName = ahead ? ahead.name.split(' ')[0] : null;
          const tight = !!aheadName && isFinite(intervalNum)
            && intervalNum > 0 && intervalNum <= BATTLE_THRESHOLD;
          const lane = (
            <Lane
              key={d.name}
              $lead={d.leader}
              $flash={flash.get(d.name) || null}
              style={{ '--c': d.color, '--g': d.glow, '--i': i } as React.CSSProperties}
            >
              <Pos $lead={d.leader}>{d.pos}</Pos>
              <Bar />
              <Who>
                <span className="nm">{d.name}</span>
              </Who>

              <Track>
                <Surface style={{ '--i': i } as React.CSSProperties}>
                  <div className="flag" />
                  <Car
                    $lead={d.leader}
                    /* --x is owned by the effects below and set NOWHERE in JSX. Two writers for
                       one property is how a poll landing mid-lap yanks a car back to its resting
                       position halfway round. */
                    ref={(el) => { if (el) carRefs.current.set(d.name, el); }}
                  >
                    <Exhaust><i /><i /><i /><i /><i /><i /></Exhaust>
                    <SideCarSvg idSuffix={d.code} />
                    {/* The empty asphalt ahead of a car is not waste — it is exactly the
                        distance still to cover. Naming it turns dead space into the one thing
                        that PM most needs to know. The leader has nothing ahead, so says nothing. */}
                    {!d.leader && (
                      <span className={`ahead${tight ? ' battle' : ''}`}>
                        {tight && aheadName
                          ? <>{d.interval} <b>TO {aheadName.toUpperCase()}</b></>
                          : <>{d.gap} <b>TO THE LEAD</b></>}
                      </span>
                    )}
                  </Car>
                </Surface>
              </Track>

              <Metric>{d.metric}</Metric>
            </Lane>
          );
          return lane;
        })}
      </Lanes>

      <KerbRow />
    </Wrap>
  );
};

/**
 * [PM-SG SMOOTH 2026-08-13] Memoised. The board shell re-renders on its own state — the page
 * rotation tick, the sweep, an announcement, the diagnostic toggle, a resize — none of which
 * change this view's props. Without this, each of those re-rendered the whole view mid-animation.
 */
export default React.memo(RaceView);
